import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OperationalSummaryCards } from './OperationalSummaryCards';

const panelCardClass = 'rounded-lg border border-slate-200 bg-white shadow-sm';
const panelHeaderClass = 'border-b border-slate-200 px-3 py-2';
const panelBodyClass = 'px-3 py-3';
const denseInputClass = 'h-8 rounded-md border-slate-200 text-sm shadow-sm';
const subsectionClass = 'space-y-2 rounded-md border border-slate-200 bg-slate-50/80 p-2';
const OPEN_STATUSES = new Set(['pending', 'in_progress', 'correction_requested']);
const innerTabsListClass = 'flex h-auto w-full flex-wrap justify-start gap-4 rounded-none border-b border-slate-200 bg-transparent px-3.5 py-0';
const innerTabsTriggerClass = 'h-9 rounded-none border-b-2 border-transparent px-0 text-xs font-medium uppercase tracking-[0.12em] text-slate-500 shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:text-slate-900';

const emptyCatalogForm = {
  id: '',
  title: '',
  description: '',
  sector: '',
  area: '',
  subarea: '',
  function_label: '',
  priority: 'medium',
  recurrence: 'daily',
  required: true,
  activity_type: 'checklist',
  applicable_role_keys: '',
  applicable_departments: '',
  applicable_store_locations: '',
  comment_suggestions: '',
  quick_reason_codes: '',
  weekly_days: '',
  monthly_days: '',
  annual_dates: '',
  execution_example: '',
  noncompliance_example: '',
  justification_hint: '',
  typical_executor: '',
  typical_location: '',
  assignment_scope: 'individual',
  allows_comment: true,
  evidence_note: '',
  creates_backlog: true,
  accumulates_backlog: true,
  tags: '',
  allow_retroactive: true,
  is_active: true,
  sort_order: 0,
};

const emptyReasonForm = { id: '', label: '', description: '', requires_comment: true, activity_types: '', sectors: '', comment_suggestions: '' };

const statusBadgeClass = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  justified: 'bg-violet-50 text-violet-700 border-violet-200',
  correction_requested: 'bg-orange-50 text-orange-700 border-orange-200',
};

const splitLines = (value) => String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);
const splitCsv = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

export const OperationalAdminTab = ({
  overview,
  catalog,
  catalogSummary,
  reasons,
  workers,
  onGenerate,
  onSaveCatalog,
  onSetCatalogStatus,
  onDeleteCatalog,
  onSaveReason,
  onReviewCorrection,
  onRefresh,
}) => {
  const [generationMode, setGenerationMode] = useState('day');
  const [generationAssignee, setGenerationAssignee] = useState('all');
  const [manualTitle, setManualTitle] = useState('');
  const [overviewQuery, setOverviewQuery] = useState('');
  const [overviewStatus, setOverviewStatus] = useState('all');
  const [overviewPriority, setOverviewPriority] = useState('all');
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogAreaFilter, setCatalogAreaFilter] = useState('all');
  const [catalogRecurrenceFilter, setCatalogRecurrenceFilter] = useState('all');
  const [catalogRoleFilter, setCatalogRoleFilter] = useState('all');
  const [catalogStoreFilter, setCatalogStoreFilter] = useState('all');
  const [catalogRequiredFilter, setCatalogRequiredFilter] = useState('all');
  const [catalogStateFilter, setCatalogStateFilter] = useState('all');
  const [catalogForm, setCatalogForm] = useState(emptyCatalogForm);
  const [reasonForm, setReasonForm] = useState(emptyReasonForm);
  const [reviewNotes, setReviewNotes] = useState({});

  const correctionRows = overview?.open_corrections || [];
  const workerOptions = useMemo(() => workers || [], [workers]);
  const catalogRows = useMemo(() => catalog || [], [catalog]);
  const filteredRows = useMemo(() => (overview?.rows || []).filter((row) => {
    const matchesQuery = !overviewQuery || [row.title, row.assignee_name, row.area, row.store_location].some((value) => String(value || '').toLowerCase().includes(overviewQuery.toLowerCase()));
    const matchesStatus = overviewStatus === 'all' || row.status === overviewStatus;
    const matchesPriority = overviewPriority === 'all' || row.priority === overviewPriority;
    return matchesQuery && matchesStatus && matchesPriority;
  }), [overview?.rows, overviewPriority, overviewQuery, overviewStatus]);

  const catalogAreas = useMemo(() => {
    const values = new Set();
    catalogRows.forEach((item) => values.add(`${item.sector || 'Área'} · ${item.area || 'Geral'}`));
    return Array.from(values).sort();
  }, [catalogRows]);

  const catalogRoles = useMemo(() => {
    const values = new Set();
    catalogRows.forEach((item) => (item.applicable_role_keys || []).forEach((role) => values.add(role)));
    return Array.from(values).sort();
  }, [catalogRows]);

  const catalogStores = useMemo(() => {
    const values = new Set();
    catalogRows.forEach((item) => (item.applicable_store_locations || []).forEach((store) => values.add(store)));
    return Array.from(values).sort();
  }, [catalogRows]);

  const filteredCatalog = useMemo(() => catalogRows.filter((item) => {
    const compositeArea = `${item.sector || 'Área'} · ${item.area || 'Geral'}`;
    const matchesQuery = !catalogQuery || [
      item.title,
      item.description,
      item.sector,
      item.area,
      item.subarea,
      item.function_label,
      item.typical_executor,
    ].some((value) => String(value || '').toLowerCase().includes(catalogQuery.toLowerCase()));
    const matchesArea = catalogAreaFilter === 'all' || compositeArea === catalogAreaFilter;
    const matchesRecurrence = catalogRecurrenceFilter === 'all' || item.recurrence === catalogRecurrenceFilter;
    const matchesRole = catalogRoleFilter === 'all' || (item.applicable_role_keys || []).includes(catalogRoleFilter);
    const matchesStore = catalogStoreFilter === 'all' || (item.applicable_store_locations || []).includes(catalogStoreFilter);
    const matchesRequired = catalogRequiredFilter === 'all' || (catalogRequiredFilter === 'required' ? item.required : !item.required);
    const matchesState = catalogStateFilter === 'all' || (catalogStateFilter === 'active' ? item.is_active : !item.is_active);
    return matchesQuery && matchesArea && matchesRecurrence && matchesRole && matchesStore && matchesRequired && matchesState;
  }), [catalogAreaFilter, catalogQuery, catalogRecurrenceFilter, catalogRequiredFilter, catalogRoleFilter, catalogRows, catalogStateFilter, catalogStoreFilter]);

  const handleCatalogSelect = (catalogId) => {
    const selected = (catalogRows || []).find((item) => item.id === catalogId);
    if (!selected) {
      setCatalogForm(emptyCatalogForm);
      return;
    }
    setCatalogForm({
      id: selected.id,
      title: selected.title || '',
      description: selected.description || '',
      sector: selected.sector || '',
      area: selected.area || '',
      subarea: selected.subarea || '',
      function_label: selected.function_label || '',
      priority: selected.priority || 'medium',
      recurrence: selected.recurrence || 'daily',
      required: Boolean(selected.required),
      activity_type: selected.activity_type || 'checklist',
      applicable_role_keys: (selected.applicable_role_keys || []).join(', '),
      applicable_departments: (selected.applicable_departments || []).join(', '),
      applicable_store_locations: (selected.applicable_store_locations || []).join(', '),
      comment_suggestions: (selected.comment_suggestions || []).join('\n'),
      quick_reason_codes: (selected.quick_reason_codes || []).join(', '),
      weekly_days: (selected.weekly_days || []).join(', '),
      monthly_days: (selected.monthly_days || []).join(', '),
      annual_dates: (selected.annual_dates || []).join(', '),
      execution_example: selected.execution_example || '',
      noncompliance_example: selected.noncompliance_example || '',
      justification_hint: selected.justification_hint || '',
      typical_executor: selected.typical_executor || '',
      typical_location: selected.typical_location || '',
      assignment_scope: selected.assignment_scope || 'individual',
      allows_comment: selected.allows_comment !== false,
      evidence_note: selected.evidence_note || '',
      creates_backlog: selected.creates_backlog !== false,
      accumulates_backlog: selected.accumulates_backlog !== false,
      tags: (selected.tags || []).join(', '),
      allow_retroactive: Boolean(selected.allow_retroactive),
      is_active: selected.is_active !== false,
      sort_order: Number(selected.sort_order || 0),
    });
  };

  const handleReasonSelect = (reasonId) => {
    const selected = (reasons || []).find((item) => item.id === reasonId);
    if (!selected) {
      setReasonForm(emptyReasonForm);
      return;
    }
    setReasonForm({
      id: selected.id,
      label: selected.label || '',
      description: selected.description || '',
      requires_comment: Boolean(selected.requires_comment),
      activity_types: (selected.activity_types || []).join(', '),
      sectors: (selected.sectors || []).join(', '),
      comment_suggestions: (selected.comment_suggestions || []).join('\n'),
    });
  };

  const submitCatalog = async () => {
    await onSaveCatalog(catalogForm.id || null, {
      title: catalogForm.title,
      description: catalogForm.description,
      sector: catalogForm.sector,
      area: catalogForm.area,
      subarea: catalogForm.subarea,
      function_label: catalogForm.function_label,
      priority: catalogForm.priority,
      recurrence: catalogForm.recurrence,
      required: catalogForm.required,
      activity_type: catalogForm.activity_type,
      applicable_role_keys: splitCsv(catalogForm.applicable_role_keys),
      applicable_job_titles: [],
      applicable_departments: splitCsv(catalogForm.applicable_departments),
      applicable_store_locations: splitCsv(catalogForm.applicable_store_locations),
      weekly_days: splitCsv(catalogForm.weekly_days).map((item) => Number(item)).filter(Boolean),
      monthly_days: splitCsv(catalogForm.monthly_days).map((item) => Number(item)).filter(Boolean),
      annual_dates: splitCsv(catalogForm.annual_dates),
      estimated_minutes: 15,
      allow_retroactive: catalogForm.allow_retroactive,
      comment_suggestions: splitLines(catalogForm.comment_suggestions),
      quick_reason_codes: splitCsv(catalogForm.quick_reason_codes),
      execution_example: catalogForm.execution_example,
      noncompliance_example: catalogForm.noncompliance_example,
      justification_hint: catalogForm.justification_hint,
      typical_executor: catalogForm.typical_executor,
      typical_location: catalogForm.typical_location,
      assignment_scope: catalogForm.assignment_scope,
      allows_comment: catalogForm.allows_comment,
      evidence_note: catalogForm.evidence_note,
      creates_backlog: catalogForm.creates_backlog,
      accumulates_backlog: catalogForm.accumulates_backlog,
      tags: splitCsv(catalogForm.tags),
      sort_order: Number(catalogForm.sort_order || 0),
      is_active: catalogForm.is_active,
    });
    setCatalogForm(emptyCatalogForm);
  };

  const submitReason = async () => {
    await onSaveReason(reasonForm.id || null, {
      label: reasonForm.label,
      description: reasonForm.description,
      requires_comment: reasonForm.requires_comment,
      activity_types: splitCsv(reasonForm.activity_types),
      sectors: splitCsv(reasonForm.sectors),
      comment_suggestions: splitLines(reasonForm.comment_suggestions),
      is_active: true,
    });
    setReasonForm(emptyReasonForm);
  };

  const generate = async () => {
    await onGenerate({
      mode: generationMode,
      assignee_user_ids: generationAssignee === 'all' ? [] : [generationAssignee],
      manual_title: manualTitle || null,
      manual_description: manualTitle ? 'Actividade criada manualmente pelo admin/gerente.' : null,
      manual_priority: 'high',
      manual_required: true,
      manual_activity_type: manualTitle ? 'manual' : 'checklist',
    });
    setManualTitle('');
  };

  const catalogSummaryItems = [
    { label: 'Catálogo', value: catalogSummary?.total ?? catalogRows.length },
    { label: 'Obrigatórias', value: catalogSummary?.required ?? catalogRows.filter((item) => item.required).length },
    { label: 'Anuais', value: catalogSummary?.annual ?? catalogRows.filter((item) => item.recurrence === 'annual').length },
    { label: 'Inactivas', value: catalogSummary?.inactive ?? catalogRows.filter((item) => item.is_active === false).length },
  ];

  const pendingRows = useMemo(() => filteredRows.filter((row) => OPEN_STATUSES.has(row.status)), [filteredRows]);
  const recurrenceSummaryRows = catalogSummary?.recurrences || [];
  const areaSummaryRows = catalogSummary?.areas || [];
  const areaFocusRows = useMemo(() => {
    if (catalogAreaFilter === 'all') {
      return catalogRows.slice(0, 16);
    }
    return catalogRows.filter((item) => `${item.sector || 'Área'} · ${item.area || 'Geral'}` === catalogAreaFilter).slice(0, 16);
  }, [catalogAreaFilter, catalogRows]);
  const recurrenceFocusRows = useMemo(() => {
    const query = catalogQuery.toLowerCase();
    return catalogRows.filter((item) => {
      const matchesRecurrence = catalogRecurrenceFilter === 'all' || item.recurrence === catalogRecurrenceFilter;
      const matchesQuery = !query || [item.title, item.description, item.sector, item.area, item.typical_executor].some((value) => String(value || '').toLowerCase().includes(query));
      return matchesRecurrence && matchesQuery;
    }).slice(0, 18);
  }, [catalogQuery, catalogRecurrenceFilter, catalogRows]);

  return (
    <div className="space-y-2" data-testid="operational-admin-tab">
      <OperationalSummaryCards summary={overview?.summary} testIdPrefix="operational-admin-summary" />

      <Tabs defaultValue="activities" className="space-y-2" data-testid="operational-admin-subtabs">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-admin-subtabs-shell">
          <TabsList className={innerTabsListClass} data-testid="operational-admin-subtabs-list">
            <TabsTrigger value="activities" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-activities-trigger">Actividades</TabsTrigger>
            <TabsTrigger value="pending" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-pending-trigger">Pendências</TabsTrigger>
            <TabsTrigger value="catalog" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-catalog-trigger">Catálogo</TabsTrigger>
            <TabsTrigger value="corrections" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-corrections-trigger">Correcções / Aprovações</TabsTrigger>
            <TabsTrigger value="recurrences" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-recurrences-trigger">Recorrências</TabsTrigger>
            <TabsTrigger value="areas" className={innerTabsTriggerClass} data-testid="operational-admin-subtab-areas-trigger">Áreas</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="activities" className="mt-0" data-testid="operational-admin-subtab-activities-content">
          <div className="grid gap-3 xl:grid-cols-[300px_minmax(0,1fr)]">
            <Card className={`sticky top-20 h-fit ${panelCardClass}`} data-testid="operational-generation-card">
              <CardHeader className={panelHeaderClass}>
                <div>
                  <CardTitle className="text-base font-semibold text-slate-900">Geração operacional</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Painel rápido para disparar rotinas sem sair da visão de gestão.</p>
                </div>
              </CardHeader>
              <CardContent className={`space-y-3 ${panelBodyClass}`}>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Modo de geração</Label>
                  <Select value={generationMode} onValueChange={setGenerationMode}>
                    <SelectTrigger className={denseInputClass} data-testid="operational-generation-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Hoje</SelectItem>
                      <SelectItem value="next_day">Dia seguinte</SelectItem>
                      <SelectItem value="week">Semana</SelectItem>
                      <SelectItem value="month">Mês</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Colaborador</Label>
                  <Select value={generationAssignee} onValueChange={setGenerationAssignee}>
                    <SelectTrigger className={denseInputClass} data-testid="operational-generation-assignee-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os elegíveis</SelectItem>
                      {workerOptions.map((worker) => <SelectItem key={worker.user_id} value={worker.user_id}>{worker.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500" htmlFor="operational-manual-title">Título manual opcional</Label>
                  <Input className={denseInputClass} id="operational-manual-title" value={manualTitle} onChange={(event) => setManualTitle(event.target.value)} data-testid="operational-manual-title-input" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="h-9 rounded-md bg-slate-900 px-3 text-sm text-white hover:bg-slate-800" onClick={generate} data-testid="operational-generate-submit-button">Gerar</Button>
                  <Button className="h-9 rounded-md border-slate-200 px-3 text-sm text-slate-900 hover:bg-slate-50" variant="outline" onClick={onRefresh} data-testid="operational-refresh-admin-button">Actualizar</Button>
                </div>
              </CardContent>
            </Card>

            <Card className={panelCardClass} data-testid="operational-overview-table-card">
              <CardHeader className={panelHeaderClass}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-900">Actividades e backlog</CardTitle>
                    <p className="mt-1 text-xs text-slate-500">Vista central de execução com filtros para leitura rápida do estado operacional.</p>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="operational-overview-toolbar">
                    <Input className={`${denseInputClass} w-[180px]`} value={overviewQuery} onChange={(event) => setOverviewQuery(event.target.value)} placeholder="Filtrar backlog" data-testid="operational-overview-query-input" />
                    <Select value={overviewStatus} onValueChange={setOverviewStatus}>
                      <SelectTrigger className={`${denseInputClass} w-[150px]`} data-testid="operational-overview-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos estados</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="completed">Concluída</SelectItem>
                        <SelectItem value="justified">Justificada</SelectItem>
                        <SelectItem value="correction_requested">Correcção pedida</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={overviewPriority} onValueChange={setOverviewPriority}>
                      <SelectTrigger className={`${denseInputClass} w-[140px]`} data-testid="operational-overview-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Prioridade</SelectItem>
                        <SelectItem value="critical">Crítica</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="low">Baixa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto px-0 pb-0 pt-0">
                <table className="w-full min-w-[940px] text-xs" data-testid="operational-overview-table">
                  <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5">Data</th>
                      <th className="px-4 py-2.5">Colaborador</th>
                      <th className="px-4 py-2.5">Actividade</th>
                      <th className="px-4 py-2.5">Área</th>
                      <th className="px-4 py-2.5">Loja</th>
                      <th className="px-4 py-2.5">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.slice(0, 22).map((row) => (
                      <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`operational-overview-row-${row.id}`}>
                        <td className="px-4 py-2.5">{row.scheduled_date}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{row.assignee_name}</td>
                        <td className="px-4 py-2.5 text-slate-700">{row.title}</td>
                        <td className="px-4 py-2.5">{row.area || '—'}</td>
                        <td className="px-4 py-2.5">{row.store_location || 'Empresa geral'}</td>
                        <td className="px-4 py-2.5"><Badge className={statusBadgeClass[row.status] || statusBadgeClass.pending} variant="outline">{row.status_label || row.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-0" data-testid="operational-admin-subtab-pending-content">
          <div className="grid gap-3 xl:grid-cols-[320px_minmax(0,1fr)]">
            <Card className={panelCardClass} data-testid="operational-admin-pending-summary-card">
              <CardHeader className={panelHeaderClass}>
                <CardTitle className="text-base font-semibold text-slate-900">Leitura de pendências</CardTitle>
              </CardHeader>
              <CardContent className={`space-y-2.5 ${panelBodyClass}`}>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="operational-admin-pending-total">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Abertas</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{pendingRows.length}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="operational-admin-pending-critical">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Críticas / altas</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{pendingRows.filter((row) => ['critical', 'high'].includes(row.priority)).length}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="operational-admin-pending-corrections">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Com correcção</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{pendingRows.filter((row) => row.status === 'correction_requested').length}</p>
                </div>
              </CardContent>
            </Card>

            <Card className={panelCardClass} data-testid="operational-admin-pending-table-card">
              <CardHeader className={panelHeaderClass}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-900">Pendências operacionais</CardTitle>
                    <p className="mt-1 text-xs text-slate-500">Vista dedicada para itens abertos, sem mistura com concluídos ou justificações.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Input className={`${denseInputClass} w-[180px]`} value={overviewQuery} onChange={(event) => setOverviewQuery(event.target.value)} placeholder="Pesquisar pendências" data-testid="operational-admin-pending-query-input" />
                    <Select value={overviewPriority} onValueChange={setOverviewPriority}>
                      <SelectTrigger className={`${denseInputClass} w-[140px]`} data-testid="operational-admin-pending-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Prioridade</SelectItem>
                        <SelectItem value="critical">Crítica</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="low">Baixa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto px-0 pb-0 pt-0">
                <table className="w-full min-w-[940px] text-xs" data-testid="operational-admin-pending-table">
                  <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5">Data</th>
                      <th className="px-4 py-2.5">Colaborador</th>
                      <th className="px-4 py-2.5">Actividade</th>
                      <th className="px-4 py-2.5">Área</th>
                      <th className="px-4 py-2.5">Prioridade</th>
                      <th className="px-4 py-2.5">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-sm text-slate-500" data-testid="operational-admin-pending-empty">Sem pendências abertas com os filtros actuais.</td>
                      </tr>
                    ) : pendingRows.slice(0, 28).map((row) => (
                      <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`operational-admin-pending-row-${row.id}`}>
                        <td className="px-4 py-2.5">{row.scheduled_date}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{row.assignee_name}</td>
                        <td className="px-4 py-2.5 text-slate-700">{row.title}</td>
                        <td className="px-4 py-2.5">{row.area || '—'}</td>
                        <td className="px-4 py-2.5">{row.priority || 'medium'}</td>
                        <td className="px-4 py-2.5"><Badge className={statusBadgeClass[row.status] || statusBadgeClass.pending} variant="outline">{row.status_label || row.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="catalog" className="mt-0" data-testid="operational-admin-subtab-catalog-content">
          <Card className={panelCardClass} data-testid="operational-catalog-workspace-card">
            <CardHeader className={`space-y-4 ${panelHeaderClass}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-900">Catálogo operacional real</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Workspace central para leitura, filtragem e manutenção estruturada do catálogo empresarial.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {catalogSummaryItems.map((item) => (
                    <Badge key={item.label} variant="outline" className="rounded-md border-slate-200 bg-slate-50 text-slate-700">{item.label}: {item.value}</Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Áreas empresariais</p>
                <div className="flex flex-wrap gap-2" data-testid="operational-catalog-area-chips">
                  <Button className="h-8 rounded-md px-3" size="sm" variant={catalogAreaFilter === 'all' ? 'default' : 'outline'} onClick={() => setCatalogAreaFilter('all')} data-testid="operational-catalog-area-chip-all">Geral</Button>
                  {(catalogSummary?.areas || []).map((item) => {
                    const value = `${item.sector} · ${item.area}`;
                    return (
                      <Button
                        key={value}
                        className="h-8 rounded-md px-3"
                        size="sm"
                        variant={catalogAreaFilter === value ? 'default' : 'outline'}
                        onClick={() => setCatalogAreaFilter(value)}
                        data-testid={`operational-catalog-area-chip-${item.sector}-${item.area}`}
                      >
                        {item.area} ({item.count})
                      </Button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="operational-catalog-toolbar">
                <Input className={`${denseInputClass} w-[190px]`} value={catalogQuery} onChange={(event) => setCatalogQuery(event.target.value)} placeholder="Pesquisar catálogo" data-testid="operational-catalog-query-input" />
                <Select value={catalogRecurrenceFilter} onValueChange={setCatalogRecurrenceFilter}>
                  <SelectTrigger className={`${denseInputClass} w-[130px]`} data-testid="operational-catalog-recurrence-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Periódicas</SelectItem>
                    <SelectItem value="daily">Diária</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                    <SelectItem value="ad_hoc">Avulsa</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={catalogRoleFilter} onValueChange={setCatalogRoleFilter}>
                  <SelectTrigger className={`${denseInputClass} w-[150px]`} data-testid="operational-catalog-role-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Por cargo</SelectItem>
                    {catalogRoles.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={catalogStoreFilter} onValueChange={setCatalogStoreFilter}>
                  <SelectTrigger className={`${denseInputClass} w-[150px]`} data-testid="operational-catalog-store-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Por loja</SelectItem>
                    {catalogStores.length === 0 ? <SelectItem value="none" disabled>Sem lojas específicas</SelectItem> : catalogStores.map((store) => <SelectItem key={store} value={store}>{store}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={catalogRequiredFilter} onValueChange={setCatalogRequiredFilter}>
                  <SelectTrigger className={`${denseInputClass} w-[140px]`} data-testid="operational-catalog-required-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Obrigatoriedade</SelectItem>
                    <SelectItem value="required">Obrigatórias</SelectItem>
                    <SelectItem value="optional">Opcionais</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={catalogStateFilter} onValueChange={setCatalogStateFilter}>
                  <SelectTrigger className={`${denseInputClass} w-[130px]`} data-testid="operational-catalog-state-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Estado</SelectItem>
                    <SelectItem value="active">Activas</SelectItem>
                    <SelectItem value="inactive">Inactivas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="px-3.5 pb-3.5 pt-0">
              <div className="grid gap-3 xl:grid-cols-[minmax(0,1.08fr)_410px]">
                <div className="overflow-auto rounded-lg border border-slate-200" data-testid="operational-catalog-table-wrapper">
                  <table className="w-full min-w-[1040px] text-xs" data-testid="operational-catalog-table">
                    <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      <tr>
                        <th className="px-4 py-2.5">Actividade</th>
                        <th className="px-4 py-2.5">Área</th>
                        <th className="px-4 py-2.5">Periodicidade</th>
                        <th className="px-4 py-2.5">Executor</th>
                        <th className="px-4 py-2.5">Aplicação</th>
                        <th className="px-4 py-2.5">Obrig.</th>
                        <th className="px-4 py-2.5">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCatalog.map((item) => (
                        <tr key={item.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => handleCatalogSelect(item.id)} data-testid={`operational-catalog-row-${item.id}`}>
                          <td className="px-4 py-2.5 align-top">
                            <p className="font-medium text-slate-900">{item.title}</p>
                            <p className="mt-1 text-[11px] text-slate-500">{item.description}</p>
                          </td>
                          <td className="px-4 py-2.5 align-top">
                            <p>{item.sector}</p>
                            <p className="text-[11px] text-slate-500">{item.area}{item.subarea ? ` · ${item.subarea}` : ''}</p>
                          </td>
                          <td className="px-4 py-2.5 align-top">
                            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{item.recurrence}</Badge>
                            {item.recurrence === 'annual' && item.annual_dates?.length ? <p className="mt-1 text-[11px] text-slate-500">{item.annual_dates.join(', ')}</p> : null}
                            {item.recurrence === 'monthly' && item.monthly_days?.length ? <p className="mt-1 text-[11px] text-slate-500">Dias: {item.monthly_days.join(', ')}</p> : null}
                          </td>
                          <td className="px-4 py-2.5 align-top">
                            <p>{item.typical_executor || item.function_label || '—'}</p>
                            <p className="mt-1 text-[11px] text-slate-500">{(item.applicable_role_keys || []).slice(0, 3).join(', ') || 'Todos'}</p>
                          </td>
                          <td className="px-4 py-2.5 align-top">
                            <p>{item.typical_location || 'Empresa geral'}</p>
                            <p className="mt-1 text-[11px] text-slate-500">{(item.applicable_store_locations || []).join(', ') || 'Multiárea'}</p>
                          </td>
                          <td className="px-4 py-2.5 align-top">{item.required ? 'Sim' : 'Não'}</td>
                          <td className="px-4 py-2.5 align-top"><Badge variant="outline" className={item.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{item.is_active ? 'Activa' : 'Inactiva'}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Card className="sticky top-20 rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-catalog-editor-card">
                  <CardHeader className={panelHeaderClass}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900">Editor do catálogo</CardTitle>
                        <p className="mt-1 text-xs text-slate-500">Edite a actividade seleccionada sem perder o contexto do catálogo à esquerda.</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button className="h-8 rounded-md px-3" size="sm" variant="outline" onClick={() => setCatalogForm(emptyCatalogForm)} data-testid="operational-catalog-new-button">Novo</Button>
                        {catalogForm.id ? <Button className="h-8 rounded-md px-3" size="sm" variant="outline" onClick={() => onSetCatalogStatus(catalogForm.id, !catalogForm.is_active)} data-testid="operational-catalog-toggle-status-button">{catalogForm.is_active ? 'Desactivar' : 'Activar'}</Button> : null}
                        {catalogForm.id ? <Button className="h-8 rounded-md px-3" size="sm" variant="outline" onClick={() => { onDeleteCatalog(catalogForm.id); setCatalogForm(emptyCatalogForm); }} data-testid="operational-catalog-delete-button">Apagar</Button> : null}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2.5 px-3.5 py-3.5">
                    <div className={subsectionClass} data-testid="operational-catalog-editor-identity-section">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Identidade e enquadramento</p>
                        <p className="mt-1 text-xs text-slate-500">Defina claramente a actividade, a área empresarial e onde ela se aplica.</p>
                      </div>
                      <Input className={denseInputClass} value={catalogForm.title} onChange={(event) => setCatalogForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Nome claro da actividade" data-testid="operational-catalog-title-input" />
                      <Textarea className="min-h-[88px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.description} onChange={(event) => setCatalogForm((prev) => ({ ...prev, description: event.target.value }))} placeholder="Descrição operacional" rows={3} data-testid="operational-catalog-description-textarea" />
                      <div className="grid gap-2 md:grid-cols-2">
                        <Input className={denseInputClass} value={catalogForm.sector} onChange={(event) => setCatalogForm((prev) => ({ ...prev, sector: event.target.value }))} placeholder="Área principal" data-testid="operational-catalog-sector-input" />
                        <Input className={denseInputClass} value={catalogForm.area} onChange={(event) => setCatalogForm((prev) => ({ ...prev, area: event.target.value }))} placeholder="Categoria" data-testid="operational-catalog-area-input" />
                        <Input className={denseInputClass} value={catalogForm.subarea} onChange={(event) => setCatalogForm((prev) => ({ ...prev, subarea: event.target.value }))} placeholder="Subárea" data-testid="operational-catalog-subarea-input" />
                        <Input className={denseInputClass} value={catalogForm.function_label} onChange={(event) => setCatalogForm((prev) => ({ ...prev, function_label: event.target.value }))} placeholder="Cargo / função" data-testid="operational-catalog-function-input" />
                        <Input className={denseInputClass} value={catalogForm.typical_executor} onChange={(event) => setCatalogForm((prev) => ({ ...prev, typical_executor: event.target.value }))} placeholder="Quem normalmente executa" data-testid="operational-catalog-executor-input" />
                        <Input className={denseInputClass} value={catalogForm.typical_location} onChange={(event) => setCatalogForm((prev) => ({ ...prev, typical_location: event.target.value }))} placeholder="Onde normalmente se aplica" data-testid="operational-catalog-location-input" />
                      </div>
                    </div>

                    <div className={subsectionClass} data-testid="operational-catalog-editor-rules-section">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Recorrência, alvo e governança</p>
                        <p className="mt-1 text-xs text-slate-500">Controle frequência, obrigatoriedade, escopo e datas operacionais especiais.</p>
                      </div>
                      <div className="grid gap-2 md:grid-cols-2">
                        <Select value={catalogForm.priority} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, priority: value }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-priority-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">Crítica</SelectItem>
                            <SelectItem value="high">Alta</SelectItem>
                            <SelectItem value="medium">Média</SelectItem>
                            <SelectItem value="low">Baixa</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.recurrence} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, recurrence: value }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-recurrence-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Diária</SelectItem>
                            <SelectItem value="weekly">Semanal</SelectItem>
                            <SelectItem value="monthly">Mensal</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                            <SelectItem value="ad_hoc">Avulsa</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.required ? 'required' : 'optional'} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, required: value === 'required' }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-required-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="required">Obrigatória</SelectItem>
                            <SelectItem value="optional">Opcional</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.assignment_scope} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, assignment_scope: value }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-scope-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="team">Equipa</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input className={denseInputClass} value={catalogForm.applicable_role_keys} onChange={(event) => setCatalogForm((prev) => ({ ...prev, applicable_role_keys: event.target.value }))} placeholder="Perfis (csv)" data-testid="operational-catalog-roles-input" />
                        <Input className={denseInputClass} value={catalogForm.applicable_departments} onChange={(event) => setCatalogForm((prev) => ({ ...prev, applicable_departments: event.target.value }))} placeholder="Departamentos (csv)" data-testid="operational-catalog-departments-input" />
                        <Input className={denseInputClass} value={catalogForm.applicable_store_locations} onChange={(event) => setCatalogForm((prev) => ({ ...prev, applicable_store_locations: event.target.value }))} placeholder="Lojas (csv)" data-testid="operational-catalog-stores-input" />
                        <Input className={denseInputClass} value={catalogForm.tags} onChange={(event) => setCatalogForm((prev) => ({ ...prev, tags: event.target.value }))} placeholder="Tags (csv)" data-testid="operational-catalog-tags-input" />
                        <Input className={denseInputClass} value={catalogForm.weekly_days} onChange={(event) => setCatalogForm((prev) => ({ ...prev, weekly_days: event.target.value }))} placeholder="Dias semanais (1,3,5)" data-testid="operational-catalog-weekly-days-input" />
                        <Input className={denseInputClass} value={catalogForm.monthly_days} onChange={(event) => setCatalogForm((prev) => ({ ...prev, monthly_days: event.target.value }))} placeholder="Dias mensais (3,10,25)" data-testid="operational-catalog-monthly-days-input" />
                        <Input className={denseInputClass} value={catalogForm.annual_dates} onChange={(event) => setCatalogForm((prev) => ({ ...prev, annual_dates: event.target.value }))} placeholder="Datas anuais (MM-DD, csv)" data-testid="operational-catalog-annual-dates-input" />
                        <Input className={denseInputClass} type="number" min="0" value={catalogForm.sort_order} onChange={(event) => setCatalogForm((prev) => ({ ...prev, sort_order: event.target.value }))} placeholder="Ordem" data-testid="operational-catalog-sort-order-input" />
                      </div>
                    </div>

                    <div className={subsectionClass} data-testid="operational-catalog-editor-guidance-section">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Guião operacional</p>
                        <p className="mt-1 text-xs text-slate-500">Melhore a clareza visual e documental da actividade para reduzir interpretações erradas.</p>
                      </div>
                      <Textarea className="min-h-[74px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.execution_example} onChange={(event) => setCatalogForm((prev) => ({ ...prev, execution_example: event.target.value }))} placeholder="Exemplo de execução" rows={2} data-testid="operational-catalog-execution-example-textarea" />
                      <Textarea className="min-h-[74px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.noncompliance_example} onChange={(event) => setCatalogForm((prev) => ({ ...prev, noncompliance_example: event.target.value }))} placeholder="Exemplo de incumprimento" rows={2} data-testid="operational-catalog-noncompliance-textarea" />
                      <Textarea className="min-h-[74px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.justification_hint} onChange={(event) => setCatalogForm((prev) => ({ ...prev, justification_hint: event.target.value }))} placeholder="Sugestão de justificação" rows={2} data-testid="operational-catalog-justification-hint-textarea" />
                      <Textarea className="min-h-[74px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.evidence_note} onChange={(event) => setCatalogForm((prev) => ({ ...prev, evidence_note: event.target.value }))} placeholder="Nota sobre prova / comentário" rows={2} data-testid="operational-catalog-evidence-note-textarea" />
                      <Textarea className="min-h-[90px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={catalogForm.comment_suggestions} onChange={(event) => setCatalogForm((prev) => ({ ...prev, comment_suggestions: event.target.value }))} placeholder="Exemplos de execução (1 por linha)" rows={3} data-testid="operational-catalog-suggestions-textarea" />
                      <Input className={denseInputClass} value={catalogForm.quick_reason_codes} onChange={(event) => setCatalogForm((prev) => ({ ...prev, quick_reason_codes: event.target.value }))} placeholder="Motivos rápidos (csv)" data-testid="operational-catalog-reasons-input" />
                    </div>

                    <div className={subsectionClass} data-testid="operational-catalog-editor-governance-section">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Backlog e governança</p>
                        <p className="mt-1 text-xs text-slate-500">Defina o comportamento da actividade quando não é concluída no período previsto.</p>
                      </div>
                      <div className="grid gap-2 md:grid-cols-2">
                        <Select value={catalogForm.allow_retroactive ? 'yes' : 'no'} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, allow_retroactive: value === 'yes' }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-retroactive-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Admite retroactivo</SelectItem>
                            <SelectItem value="no">Sem retroactivo</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.allows_comment ? 'yes' : 'no'} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, allows_comment: value === 'yes' }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-comment-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Admite prova/comentário</SelectItem>
                            <SelectItem value="no">Sem prova/comentário</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.creates_backlog ? 'yes' : 'no'} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, creates_backlog: value === 'yes' }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-backlog-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Pode gerar pendência</SelectItem>
                            <SelectItem value="no">Sem pendência</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={catalogForm.accumulates_backlog ? 'yes' : 'no'} onValueChange={(value) => setCatalogForm((prev) => ({ ...prev, accumulates_backlog: value === 'yes' }))}>
                          <SelectTrigger className={denseInputClass} data-testid="operational-catalog-accumulates-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Pode acumular</SelectItem>
                            <SelectItem value="no">Não acumula</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="sticky bottom-0 flex justify-end border-t border-slate-200 bg-white pt-3">
                      <Button className="h-9 rounded-md bg-slate-900 px-4 text-sm text-white hover:bg-slate-800" onClick={submitCatalog} data-testid="operational-catalog-save-button">Guardar catálogo</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corrections" className="mt-0" data-testid="operational-admin-subtab-corrections-content">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
            <Card className={panelCardClass} data-testid="operational-reasons-manager-card">
              <CardHeader className={panelHeaderClass}>
                <CardTitle className="text-base font-semibold text-slate-900">Motivos / justificações</CardTitle>
              </CardHeader>
              <CardContent className={`space-y-2 ${panelBodyClass}`}>
                <Select value={reasonForm.id || 'new'} onValueChange={(value) => handleReasonSelect(value === 'new' ? '' : value)}>
                  <SelectTrigger className={denseInputClass} data-testid="operational-reason-select"><SelectValue placeholder="Novo motivo ou editar existente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Novo motivo</SelectItem>
                    {(reasons || []).map((item) => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className={denseInputClass} value={reasonForm.label} onChange={(event) => setReasonForm((prev) => ({ ...prev, label: event.target.value }))} placeholder="Rótulo" data-testid="operational-reason-label-input" />
                <Textarea className="min-h-[78px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={reasonForm.description} onChange={(event) => setReasonForm((prev) => ({ ...prev, description: event.target.value }))} placeholder="Descrição" data-testid="operational-reason-description-textarea" />
                <Input className={denseInputClass} value={reasonForm.activity_types} onChange={(event) => setReasonForm((prev) => ({ ...prev, activity_types: event.target.value }))} placeholder="Tipos de actividade (csv)" data-testid="operational-reason-activity-types-input" />
                <Input className={denseInputClass} value={reasonForm.sectors} onChange={(event) => setReasonForm((prev) => ({ ...prev, sectors: event.target.value }))} placeholder="Sectores (csv)" data-testid="operational-reason-sectors-input" />
                <Textarea className="min-h-[90px] rounded-md border-slate-200 bg-white text-sm shadow-sm" value={reasonForm.comment_suggestions} onChange={(event) => setReasonForm((prev) => ({ ...prev, comment_suggestions: event.target.value }))} placeholder="Sugestões (1 por linha)" data-testid="operational-reason-suggestions-textarea" />
                <Button className="h-9 rounded-md bg-slate-900 px-4 text-sm text-white hover:bg-slate-800" onClick={submitReason} data-testid="operational-reason-save-button">Guardar motivo</Button>
              </CardContent>
            </Card>

            <Card className={panelCardClass} data-testid="operational-corrections-card">
              <CardHeader className={panelHeaderClass}>
                <CardTitle className="text-base font-semibold text-slate-900">Correcções e aprovações</CardTitle>
              </CardHeader>
              <CardContent className={`space-y-3 ${panelBodyClass}`}>
                {correctionRows.length === 0 ? <p data-testid="operational-corrections-empty">Sem pedidos de correcção abertos.</p> : correctionRows.map((row) => (
                  <div key={row.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={`operational-correction-row-${row.id}`}>
                    <p className="font-medium text-slate-900">{row.assignee_name}</p>
                    <p className="mt-1 text-[11px] text-slate-600">{row.request_note}</p>
                    <Textarea className="mt-2 min-h-[76px] rounded-md border-slate-200 bg-white text-sm shadow-sm" rows={2} value={reviewNotes[row.id] || ''} onChange={(event) => setReviewNotes((prev) => ({ ...prev, [row.id]: event.target.value }))} data-testid={`operational-correction-review-note-${row.id}`} />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" className="h-8 rounded-md bg-slate-900 px-3 text-sm text-white hover:bg-slate-800" onClick={() => onReviewCorrection(row.id, { decision: 'approved', review_note: reviewNotes[row.id] || 'Correcção aprovada', apply_status: row.requested_changes?.status || null, apply_executed_at: row.requested_changes?.executed_at || null, apply_comment: row.requested_changes?.comment || null, apply_reason_id: row.requested_changes?.reason_id || null })} data-testid={`operational-correction-approve-${row.id}`}>Aprovar</Button>
                      <Button size="sm" className="h-8 rounded-md border-slate-200 px-3 text-sm text-slate-900 hover:bg-slate-50" variant="outline" onClick={() => onReviewCorrection(row.id, { decision: 'rejected', review_note: reviewNotes[row.id] || 'Correcção rejeitada' })} data-testid={`operational-correction-reject-${row.id}`}>Rejeitar</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="recurrences" className="mt-0" data-testid="operational-admin-subtab-recurrences-content">
          <div className="grid gap-3 xl:grid-cols-4">
            {recurrenceSummaryRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-admin-recurrences-empty">Sem recorrências agregadas.</p> : recurrenceSummaryRows.map((item) => (
              <Card key={item.key} className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`operational-admin-recurrence-card-${item.key}`}>
                <CardContent className="px-3.5 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{item.key}</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{item.count}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Itens do catálogo nesta recorrência.</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className={`${panelCardClass} mt-3`} data-testid="operational-admin-recurrence-table-card">
            <CardHeader className={panelHeaderClass}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-900">Mapa de recorrências</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Leitura dedicada das actividades por periodicidade.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Input className={`${denseInputClass} w-[190px]`} value={catalogQuery} onChange={(event) => setCatalogQuery(event.target.value)} placeholder="Pesquisar recorrências" data-testid="operational-admin-recurrence-query-input" />
                  <Select value={catalogRecurrenceFilter} onValueChange={setCatalogRecurrenceFilter}>
                    <SelectTrigger className={`${denseInputClass} w-[130px]`} data-testid="operational-admin-recurrence-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Periódicas</SelectItem>
                      <SelectItem value="daily">Diária</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                      <SelectItem value="annual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-auto px-0 pb-0 pt-0">
              <table className="w-full min-w-[900px] text-xs" data-testid="operational-admin-recurrence-table">
                <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Actividade</th>
                    <th className="px-4 py-2.5">Recorrência</th>
                    <th className="px-4 py-2.5">Área</th>
                    <th className="px-4 py-2.5">Executor</th>
                  </tr>
                </thead>
                <tbody>
                  {recurrenceFocusRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-sm text-slate-500" data-testid="operational-admin-recurrence-table-empty">Sem actividades com os filtros actuais.</td>
                    </tr>
                  ) : recurrenceFocusRows.map((item) => (
                    <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`operational-admin-recurrence-row-${item.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <p className="mt-1 text-[11px] text-slate-500">{item.description}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{item.recurrence}</Badge>
                      </td>
                      <td className="px-4 py-2.5">{item.sector} · {item.area}</td>
                      <td className="px-4 py-2.5">{item.typical_executor || item.function_label || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="areas" className="mt-0" data-testid="operational-admin-subtab-areas-content">
          <div className="grid gap-3 xl:grid-cols-[360px_minmax(0,1fr)]">
            <Card className={panelCardClass} data-testid="operational-admin-areas-card">
              <CardHeader className={panelHeaderClass}>
                <CardTitle className="text-base font-semibold text-slate-900">Áreas empresariais</CardTitle>
              </CardHeader>
              <CardContent className={`space-y-2 ${panelBodyClass}`}>
                {areaSummaryRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-admin-areas-empty">Sem áreas agregadas.</p> : areaSummaryRows.map((item) => (
                  <div key={item.key} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-admin-area-row-${item.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{item.area}</p>
                      <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{item.count}</Badge>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{item.sector}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className={panelCardClass} data-testid="operational-admin-areas-focus-card">
              <CardHeader className={panelHeaderClass}>
                <div className="space-y-2">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-900">Leitura por área</CardTitle>
                    <p className="mt-1 text-xs text-slate-500">SubAba dedicada para perceber rapidamente onde cada actividade está concentrada.</p>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="operational-admin-areas-chip-list">
                    <Button className="h-8 rounded-md px-3" size="sm" variant={catalogAreaFilter === 'all' ? 'default' : 'outline'} onClick={() => setCatalogAreaFilter('all')} data-testid="operational-admin-area-chip-all">Geral</Button>
                    {areaSummaryRows.map((item) => {
                      const value = `${item.sector} · ${item.area}`;
                      return (
                        <Button key={value} className="h-8 rounded-md px-3" size="sm" variant={catalogAreaFilter === value ? 'default' : 'outline'} onClick={() => setCatalogAreaFilter(value)} data-testid={`operational-admin-area-chip-${item.area}`}>
                          {item.area}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto px-0 pb-0 pt-0">
                <table className="w-full min-w-[860px] text-xs" data-testid="operational-admin-areas-table">
                  <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5">Actividade</th>
                      <th className="px-4 py-2.5">Área</th>
                      <th className="px-4 py-2.5">Recorrência</th>
                      <th className="px-4 py-2.5">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaFocusRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-sm text-slate-500" data-testid="operational-admin-areas-table-empty">Sem actividades para a área seleccionada.</td>
                      </tr>
                    ) : areaFocusRows.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`operational-admin-area-focus-row-${item.id}`}>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-slate-900">{item.title}</p>
                          <p className="mt-1 text-[11px] text-slate-500">{item.description}</p>
                        </td>
                        <td className="px-4 py-2.5">{item.sector} · {item.area}</td>
                        <td className="px-4 py-2.5">{item.recurrence}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline" className={item.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{item.is_active ? 'Activa' : 'Inactiva'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};